import { useMemo, useState } from "react";
import { TicketService } from "../../../services/api";
import { TableCell } from "@mui/material";
import { PaginatedListTicket, Ticket } from "../../../services/swagger";
import { useNavigate } from "react-router";
import PriorityChip from "../../atoms/PriorityChip";
import AvatarWithInitials from "../../atoms/AvatarWithInitials";
import TicketStatus from "../../atoms/TicketStatus";
import { useGlobalContext } from "../../../stores/Global";
import { useTranslation } from "react-i18next";
import ItemCard from "../../organisms/ItemCard";
import DataView, { QueryParameters } from "../../organisms/DataView";
import { useQuery } from "@tanstack/react-query";

export default function TicketsPage() {
  const { t } = useTranslation("Ticket");
  const { context } = useGlobalContext();
  const navigate = useNavigate();
  const [query, setQuery] = useState<QueryParameters>();

  const getTicketReadStatus = useMemo(() => {
    if (!context.user) return (ticket: Ticket) => ticket;
    return (ticket: Ticket): Ticket & { unRead?: boolean } => {
      const username = context.user?.username || "";
      const _id = context.user?._id || "";
      let userUpdateById = ticket.usersUpdated.find((userUpdate) => {
        if (userUpdate.user) return (userUpdate.user as any) === _id;
      });
      let userUpdateByUsername = ticket.usersUpdated.find((userUpdate) => {
        if (typeof (userUpdate as any).username === "string")
          return (userUpdate as any).username === username;
      });
      const userUpdate = userUpdateById || userUpdateByUsername;
      const read = (userUpdate?.lastUpdate || 0) > ticket.lastUpdate;
      if (userUpdate) {
        return userUpdate.lastUpdate >= ticket.lastUpdate
          ? { ...ticket, unRead: !read }
          : { ...ticket, unRead: !read };
      } else return { ...ticket, unRead: !read };
    };
  }, [context.user]);

  const loadTickets = async (): Promise<PaginatedListTicket | null> => {
    if (!query) return null;
    const res = await TicketService.getTicketList({
      search: query.search,
      limit: query.limit,
      skip: query.skip,
      filters: query.filters,
      sortDirection: query.sortDirection,
      sortField: query.sortField,
    });
    if (res?.data?.data) return res.data.data;
    else return null;
  };

  const { data: tickets, isPlaceholderData: isLoading } = useQuery({
    queryFn: loadTickets,
    queryKey: ["tickets", { query }],
    refetchInterval: 1000 * 10,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    placeholderData: (previousData) => previousData,
    maxPages: 10,
  });

  const navigateToTicket = (ticket: Ticket) => {
    navigate(`/m/ticket/${ticket._id}`);
  };

  const handleDataTableChange = (query: QueryParameters) => {
    setQuery(query);
  };

  return (
    <DataView
      name={t("Tickets")}
      loading={isLoading || !tickets}
      button={{
        text: t("NewTicket"),
        onClick: () => navigate(`/m/ticket/create`),
      }}
      onItemClick={(item) => navigateToTicket(item)}
      pagination
      count={tickets?.count || 0}
      items={
        context.user
          ? tickets?.docs.map((ticket) => getTicketReadStatus(ticket)) || []
          : []
      }
      headers={[
        {
          field: "person",
          name: t("Person"),
          type: "other",
          renderHeaderCell: () => (
            <TableCell width={20}>{t("Person")}</TableCell>
          ),
          renderRowCell: (row: Ticket) => {
            return (
              <TableCell>
                <AvatarWithInitials>
                  {row.user?.name || row.person || ""}
                </AvatarWithInitials>
              </TableCell>
            );
          },
        },
        {
          field: "id",
          name: t("ID"),
          type: "number",
        },
        {
          field: "date",
          name: t("Date"),
          type: "date",
        },
        {
          field: "companyName",
          name: t("Company"),
          type: "string",
        },
        {
          field: "title",
          name: t("Title"),
          type: "other",
          renderRowCell: (row: Ticket) => (
            <TableCell
              sx={{
                overflow: "hidden",
                whiteSpace: "nowrap",
                maxWidth: 300,
                textOverflow: "ellipsis",
              }}
            >
              {row?.title}
            </TableCell>
          ),
        },
        {
          field: "department",
          name: t("Department"),
          type: "other",
          renderRowCell: (row: Ticket) => {
            return <TableCell>{row?.department?.name}</TableCell>;
          },
        },
        {
          field: "status",
          name: t("Status"),
          type: "other",
          renderRowCell: (row: Ticket) => {
            return (
              <TableCell>
                <TicketStatus status={row.status} />
              </TableCell>
            );
          },
        },
        {
          field: "service_call.call_id",
          name: t("ServiceCallNumber"),
          type: "other",
          renderRowCell: (row: Ticket) => {
            return (
              <TableCell>
                {row.service_call?.call_id || t("NotAvailable")}
              </TableCell>
            );
          },
        },
        {
          field: "location",
          name: t("Location"),
          type: "other",
          renderRowCell: (row: Ticket) => (
            <TableCell
              sx={{
                overflow: "hidden",
                whiteSpace: "nowrap",
                maxWidth: 200,
                textOverflow: "ellipsis",
              }}
            >
              {[row.city, row.location].filter((e) => Boolean(e)).join(", ")}
            </TableCell>
          ),
        },
        {
          field: "priority",
          name: t("Priority"),
          type: "other",
          renderHeaderCell: () => (
            <TableCell width={50} align="center">
              {t("Priority")}
            </TableCell>
          ),
          renderRowCell: (row: Ticket) => {
            return (
              <TableCell align="center">
                <PriorityChip priority={row.priority} />
              </TableCell>
            );
          },
        },
      ]}
      queryFunction={TicketService.getTicketListQuery}
      onChange={handleDataTableChange}
      renderItem={(ticket) => (
        <ItemCard ticket={ticket} onClick={() => navigateToTicket(ticket)} />
      )}
    />
  );
}
